import {
  User,
  UserManager,
  WebStorageStateStore,
  type OidcMetadata,
} from "oidc-client-ts";
import type { AuthConfigDto } from "../api/types";

let manager: UserManager | null = null;

/**
 * Builds browser-safe OIDC metadata without cross-origin discovery requests.
 * @param config - Public authentication configuration returned by the API.
 * @returns Connect endpoints plus the same-origin JWKS proxy endpoint.
 */
export function buildOidcMetadata(config: AuthConfigDto): Partial<OidcMetadata> {
  const authority = config.authority.replace(/\/+$/, "");
  const appOrigin = new URL(config.redirectUri).origin;
  return {
    issuer: authority,
    authorization_endpoint: `${authority}/connect/authorize`,
    token_endpoint: `${authority}/connect/token`,
    end_session_endpoint: `${authority}/connect/endsession`,
    jwks_uri: `${appOrigin}/api/auth/jwks`,
  };
}

/**
 * Creates a PKCE UserManager from public API auth configuration.
 * @param config - Public authentication configuration returned by the API.
 * @returns The configured PKCE user manager.
 */
export function createUserManager(config: AuthConfigDto): UserManager {
  manager = new UserManager({
    authority: config.authority,
    metadata: buildOidcMetadata(config),
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: "code",
    scope: config.scope,
    loadUserInfo: false,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  });
  return manager;
}

export function getUserManager(): UserManager {
  if (manager === null)
    throw new Error("Innlogging er ikke initialisert.");
  return manager;
}

export async function getAccessToken(): Promise<string | null> {
  if (manager === null)
    return null;
  const user: User | null = await manager.getUser();
  if (user === null || user.expired)
    return null;
  return user.access_token;
}
